<template>
  <UniversalList v-model:pagination-options="options" v-model:search-term="searchTerms" v-model:sort-options="sortBy" :title="pageTitle"
                 :headers="headers"
                 :display-header="displayHeader" :fetch-data="fetchData"
                 :asset-fetcher="assetFetcher" :list="orders" :total-items="totalItems" asset-key="id">

    <template v-slot:item.id="{item}">
      <v-chip :text="item.id" border="thin opacity-25" class="hover_color_pro-hellblau" label
              prepend-icon="mdi-book" @click="openOrderInformationDialog(item.id)">
        <template v-slot:prepend>
          <v-icon color="medium-emphasis"></v-icon>
        </template>
      </v-chip>
    </template>

    <template v-if="globalSettings.isMobile" v-slot:body>
      <v-col
          v-for="item in orders"
          :key="item.id"
          cols="12"
      >
        <v-card class="pa-3" elevation="1" rounded="lg">
          <div class="d-flex align-center justify-space-between mb-2">
            <div class="d-flex align-center">
              <v-icon icon="mdi-clipboard-text" class="me-2" size="20"/>
              <span class="text-h6 font-weight-bold">Auftrag #{{ item.id }}</span>
            </div>
            <v-chip :color="StateColor[item.state]" size="small"> {{ item.state }}</v-chip>
          </div>

          <v-divider class="my-2"></v-divider>

          <div class="text-body-2 mb-2">
            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-text" class="me-3" size="18" color="grey-darken-1"/>
              <span>{{ item.title }}</span>
            </div>

            <div class="d-flex align-center mb-2" v-if="item.description">
              <v-icon icon="mdi-comment-text" class="me-3" size="18" color="grey-darken-1"/>
              <span class="text-truncate">{{ item.description }}</span>
            </div>

            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-clock" class="me-3" size="18" color="grey-darken-1"/>
              <span>{{ item.scheduled_duration }}</span>
            </div>

            <div class="d-flex align-center mb-2">
              <v-icon icon="mdi-factory" class="me-3" size="18" color="grey-darken-1"/>
              <span>{{ item.asset_id }}</span>
            </div>

            <div class="d-flex align-center mb-2" v-if="item.scheduled_start">
              <v-icon icon="mdi-calendar-clock" class="me-3" size="18" color="grey-darken-1"/>
              <span>{{ new Date(item.scheduled_start).toLocaleDateString() }} {{
                  new Date(item.scheduled_start).toLocaleTimeString([], {
                    hour: '2-digit',
                    minute: '2-digit'
                  })
                }} Uhr</span>
            </div>
          </div>

          <v-btn
              block
              prepend-icon="mdi-eye"
              :variant="globalSettings.globalButtonVariant"
              color="btnColor"
              size="small"
              @click="openOrderInformationDialog(item.id)"
          >
            Details anzeigen
          </v-btn>
        </v-card>
      </v-col>
    </template>
  </UniversalList>
</template>

<script setup lang="ts">
import UniversalList, {PaginationOptions} from "@/components/misc/UniversalList.vue";
import {openOrderInformationDialog, t, useGlobalSetting} from "@/lib/utils";
import {reactive, ref} from "vue";
import {Groups, Order, StateColor} from "@/types/Order";
import {AssetFetchParams, AssetItem} from "@/types/Asset";
import {SortItem} from "vuetify/lib/components/VDataTable/composables/sort";
import {Header} from "@/lib/types";
import StateSelect from "@/components/inputs/StateSelect.vue";
import {getCompressedOrders, getOrdersFiltered} from "@/api/orders";
import {getCompressedTasks} from "@/api/tasks";

const globalSettings = useGlobalSetting();

let orders = reactive<Order[]>([]);

interface Props {
  defaultFilters?: Record<string, any>
  pageTitle?: string
  icon?: string
}

const props = withDefaults(defineProps<Props>(), {
  defaultFilters: () => ({}),
  pageTitle: 'Aufträge'
})

const searchTerms = ref({
  ...props.defaultFilters
});

const options = ref<PaginationOptions>({page: 1, itemsPerPage: 5});
const totalItems = ref(0);
const pages = ref(0);
const sortBy = ref<SortItem[]>([{key: '', order: 'asc'}]);

const titleList = ref<AssetItem[]>();

const displayHeader: Header[] = [
  {title: "Auftragsnummer", key: "id", align: "center", icon: "mdi-numeric"},
  {title: "Title", key: "title", align: "center", icon: "mdi-format-title"},
  {title: "Author", key: "author", align: "center", icon: "mdi-account"},
  {title: "Anlage", key: "asset_id", align: "center", icon: "mdi-factory"},
  {title: "Dauer", key: "scheduled_duration", align: "center", icon: "mdi-clock-time-eight"},
  {title: "SollStart", key: "scheduled_start", align: "center", icon: "mdi-calendar-range"}
]

const headers: Header[] = [
  {title: 'Auftragsnummer', key: 'id', sortable: true, icon: 'mdi-identifier', align: 'center'},
  {title: 'Titel', key: 'title', sortable: true, icon: 'mdi-format-title', align: 'center'},
  {title: 'Autor', key: 'author', sortable: true, icon: 'mdi-account', align: 'center'},
  {
    title: 'Status',
    key: 'state',
    sortable: true,
    icon: 'mdi-checkbox-marked-circle-outline',
    align: 'center',
    filterComponent: StateSelect,
    filterProps: {permission: Groups.admin.permission}
  },
  {title: 'Beschreibung', key: 'description', sortable: false, icon: 'mdi-text-long', align: 'center'},
  {title: 'Anlage', key: 'asset_id', sortable: true, icon: 'mdi-factory', align: 'center'},
  {title: 'Dauer', key: 'scheduled_duration', sortable: true, icon: 'mdi-timer-outline', align: 'center'},
  {title: 'SollStart', key: 'scheduled_start', sortable: true, icon: 'mdi-calendar-start', align: 'center'},
  {title: 'SollEnde', key: 'scheduled_end', sortable: true, icon: 'mdi-calendar-end', align: 'center'},
  {title: 'Stillstandcode', key: 'outage_code', sortable: true, icon: 'mdi-pause-circle-outline', align: 'center'}
]

headers.forEach(header => {
  if (header.key in searchTerms.value) {
    header.disabled = true;
  }
})

displayHeader.forEach(header => {
  if (header.key in searchTerms.value) {
    header.disabled = true;
  }
})

const fetchData = async () => {
  globalSettings.loadingMessage = t("loading.getAuftragsListeData");
  globalSettings.isLoading = true;
  const {page, itemsPerPage} = options.value;
  const params = {
    page,
    itemsPerPage,
    sortBy: sortBy.value?.[0]?.key || "id",
    sortOrder: sortBy.value?.[0]?.order === "desc" ? "DESC" : "ASC",
    filters: searchTerms.value
  };

//apiClient.get("/getAuftragsListeData", {params});
  const response = await getOrdersFiltered(params)
  orders.length = 0;
  Object.assign(orders, response.items);
  orders.forEach(order => {
    order.id = Number(order.id);
  })
  totalItems.value = response.total;
  pages.value = Math.ceil(totalItems.value / options.value.itemsPerPage);
  globalSettings.isLoading = false;
  /*getAuftragsTitleList().then(data => {
    titleList.value = data.map(asset => {
          return {
            title: `${asset.title}`,
            subtitle: asset.id,
          }
        }
    );
    globalSettings.isLoading = false;
  });*/
};

const assetFetcher = (params: AssetFetchParams) => getCompressedOrders(params, props.defaultFilters);
</script>